import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UpsellIcon } from '~/components/UpsellIcons';
import { ExportInvoices } from '~/components/invoices/ExportInvoices';
import useAccountingShouldDoFirstConnection from '~/hooks/useAccountingShouldDoFirstConnection';
import { useAnalytics } from '~/hooks/useAnalytics';
import { useHasInvoicing, useHasSeeInvoices } from '~/hooks/useFeatureFlag';
import useRouteMatch from '~/hooks/useRouteMatch';
import IconDocument from '~/icons/icon-document.svg?react';
import { useModal } from '~/modals/Modal';
import { RouteConfig } from '~/routes/routeConfig';
import { AnalyticsEvents } from '~/services/analytics';
import { useIsSubmenuOpen, useSetSubmenu } from '~/state/navigationSidebar';
import { UserRole } from '~/types/company';
import { SubmenuSection } from '~/types/sidebar';
import NavigationSidebarItem from '../NavigationSidebarItem';

interface Props {
  disabled?: boolean;
}

const InvoicesItem: React.FC<Props> = ({ disabled }) => {
  const { t } = useTranslation('sidebar');
  const { track } = useAnalytics();
  const { showModal } = useModal();
  const isSubmenuOpen = useIsSubmenuOpen('invoices');
  const openSubmenu = useSetSubmenu('invoices');

  const hasInvoicing = useHasInvoicing([UserRole.ENTREPRENEUR]);
  const hasSeeInvoices = useHasSeeInvoices();
  const showInvoicingUpsell = hasSeeInvoices && !hasInvoicing;
  const shouldDoFirstConnection = useAccountingShouldDoFirstConnection();

  const badge = shouldDoFirstConnection === 'first_connection' ? 1 : 0;

  const handleExport = useCallback(() => {
    showModal({
      component: ExportInvoices,
      props: {},
    });
  }, [showModal]);

  const sections: SubmenuSection[] = useMemo(
    () => [
      {
        label: t('invoices.submenu.overview'),
        items: [
          { label: t('invoices.submenu.situation'), href: `${RouteConfig.Invoices.buildLink()}/dashboard` },
          { label: t('invoices.submenu.issued'), href: `${RouteConfig.Invoices.buildLink()}/issued` },
          { label: t('invoices.submenu.received'), href: `${RouteConfig.Invoices.buildLink()}/received` },
          { label: t('invoices.submenu.bills'), href: `${RouteConfig.Invoices.buildLink()}/bills` },
          { label: t('invoices.submenu.ddt'), href: RouteConfig.InvoicesDdt.buildLink() },
          { label: t('invoices.submenu.quotes'), href: RouteConfig.InvoicesQuotes.buildLink() },
        ],
      },
      {
        label: t('invoices.submenu.manage'),
        items: [
          { label: t('invoices.submenu.counterparts'), href: RouteConfig.Counterparts.buildLink() },
          ...(hasInvoicing
            ? [{ label: t('invoices.submenu.product_catalog'), href: RouteConfig.InvoicesProductCatalog.buildLink() }]
            : []),
          {
            label: t('invoices.submenu.profile'),
            href: `${RouteConfig.Invoices.buildLink()}/profile`,
            matchSubRoutes: true,
          },
        ],
      },
      {
        label: t('invoices.submenu.quick_actions'),
        items: [
          ...(hasSeeInvoices
            ? [
                showInvoicingUpsell
                  ? {
                      icon: UpsellIcon.Variant,
                      label: t('invoices.submenu.create'),
                      href: RouteConfig.InvoicesInfo.buildLink(),
                      onClick: () => track(AnalyticsEvents.CREATE_NEW_DOCUMENT_CLICKED, { type: 'invoice' }),
                    }
                  : {
                      label: t('invoices.submenu.create'),
                      href: `${RouteConfig.Invoices.buildLink()}/builder`,
                      onClick: () => track(AnalyticsEvents.CREATE_NEW_DOCUMENT_CLICKED, { type: 'invoice' }),
                    },
              ]
            : []),
          { label: t('invoices.submenu.import'), href: `${RouteConfig.Invoices.buildLink()}/import` },
          { label: t('invoices.submenu.export'), action: handleExport },
        ],
      },
    ],
    [t, hasInvoicing, hasSeeInvoices, showInvoicingUpsell, track, handleExport],
  );

  const isActive = Boolean(useRouteMatch(sections.flatMap((s) => s.items.map((i) => i.href ?? ''))));

  return (
    <NavigationSidebarItem
      disabled={disabled}
      href={`${RouteConfig.Invoices.buildLink()}/dashboard`}
      iconComponent={IconDocument}
      isSubmenuOpen={isSubmenuOpen}
      notificationCount={badge}
      onOpenSubmenu={openSubmenu}
      title={t('invoices.label')}
      isActive={isActive}
      submenuSections={badge === 1 ? undefined : sections}
    />
  );
};

export default memo(InvoicesItem);
